'use client';

import Image from 'next/image';
import Link from 'next/link';

import {
  CarouselArrowControls,
  CarouselDots,
  CarouselTrack,
  useCarousel,
} from '@/components/site/carousel';

const VISIBLE_COUNT = { base: 1, md: 2 } as const;

const TESTIMONIALS = [
  {
    name: 'Charlisa Boyd',
    market: 'Sold to Opendoor in Raleigh, NC',
    quote:
      '“To them it’s not about the sale, it’s about trying to help families move on. They treated me like I was their only client, and I had that one-on-one attention.”',
    imageSrc:
      'https://images.opendoor.com/source/s3/imgdrop-production/2024-02-1707764045916-55784.png?service=cosmos',
    href: '/articles/charlisa-boyd',
  },
  {
    name: 'Adam Leon',
    market: 'Sold to Opendoor in Phoenix, AZ',
    quote:
      '“Opendoor’s offer came in right near our appraisal, but we never had to list the house or do showings. For the kind of value Opendoor gives you, it’s just a no-brainer.”',
    imageSrc:
      'https://images.opendoor.com/source/s3/imgdrop-production/2024-02-1707767033539-52304.png?service=cosmos',
    href: '/articles/adam-leon',
  },
  {
    name: 'George and Carol Bertoty',
    market: 'Sold to Opendoor in Henderson, NV',
    quote:
      '“We’re of the age where we didn’t want to do a lot of work on the house. We just wanted out of there! Move on with life—and Opendoor helped us do that.”',
    imageSrc:
      'https://images.opendoor.com/source/s3/imgdrop-production/2024-02-1707767425435-95062.png?service=cosmos',
    href: '/articles/george-and-carol-bertoty',
  },
] as const;

const testimonialArrowClass = (inactive: boolean) =>
  `flex h-11 w-11 items-center justify-center rounded-full text-ink transition text-subheader-md ${
    inactive ? 'cursor-not-allowed bg-canvas-deep opacity-40' : 'hover:bg-state-hover'
  }`;

function TestimonialCard({
  testimonial,
  tabIndex,
}: {
  testimonial: (typeof TESTIMONIALS)[number];
  tabIndex?: number;
}) {
  return (
    <article className="flex min-h-[32rem] flex-col items-center rounded-32x bg-canvas-deep p-8 text-center sm:p-10">
      <Image
        src={testimonial.imageSrc}
        alt=""
        width={128}
        height={128}
        className="h-32 w-32 rounded-full object-cover"
        unoptimized
      />
      <blockquote className="mt-8 leading-relaxed text-ink text-body-lg">
        {testimonial.quote}
      </blockquote>
      <Link
        href={testimonial.href}
        prefetch={false}
        tabIndex={tabIndex}
        className="mt-8 font-medium text-accent transition hover:text-accent-dark text-body-md"
      >
        Read more
      </Link>
      <div className="mt-auto pt-12">
        <p className="font-medium text-ink text-body-lg">{testimonial.name}</p>
        <p className="mt-1 font-medium text-ink-muted text-body-sm">{testimonial.market}</p>
      </div>
    </article>
  );
}

export function CustomerTestimonialsSection() {
  const carousel = useCarousel({ itemCount: TESTIMONIALS.length, visibleCount: VISIBLE_COUNT });

  return (
    <section
      className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12"
      aria-labelledby="customer-stories-heading"
    >
      <div className="lg:col-span-4">
        <h2
          id="customer-stories-heading"
          className="font-medium leading-tight tracking-tight text-ink text-header-md sm:text-header-lg"
        >
          <span className="text-accent">Join thousands of customers</span> who made their move with
          Opendoor
        </h2>
        <CarouselArrowControls
          previous={carousel.previous}
          next={carousel.next}
          atStart={carousel.atStart}
          atEnd={carousel.atEnd}
          previousLabel="Show previous customer stories"
          nextLabel="Show next customer stories"
          previousIcon={<span aria-hidden>←</span>}
          nextIcon={<span aria-hidden>→</span>}
          buttonClassName={testimonialArrowClass}
          className="mt-10 flex items-center gap-4"
        />
      </div>
      <div className="overflow-hidden lg:col-span-8" aria-live="polite">
        <CarouselTrack
          items={TESTIMONIALS}
          getKey={(testimonial) => testimonial.name}
          current={carousel.current}
          itemWidth={carousel.itemWidth}
          visibleCount={carousel.visibleCount}
          renderItem={({ item, tabIndex }) => (
            <TestimonialCard testimonial={item} tabIndex={tabIndex} />
          )}
        />

        {carousel.paginated && (
          <CarouselDots
            count={carousel.maxPosition + 1}
            current={carousel.current}
            goToPosition={carousel.goToPosition}
            labelPrefix="Go to customer stories position"
          />
        )}
      </div>
    </section>
  );
}
